import { ReactNode, ReactElement, useState, useRef, useEffect } from 'react';
import {
  CancelToken,
  ICanceller,
} from 'app/utils/cancelToken';
import { useLatestCallback, useConstant } from 'app/utils/react';
import TaskQueue from 'app/utils/queues/TaskQueue';

export enum LoadingStage {
  // `load()` rejected, and the `result` is the value that it rejected with.
  Error = 'error',
  // There is no result (either because the component has just mounted or
  // because `clearResult()` was called), and the component is not loading anything.
  Idle = 'idle',
  // There is no result, and the component is currently loading (waiting for
  // `load()` to resolve or reject).
  Loading = 'loading',
  // `load()` resolved, and the `result` is the value that it resolved with.
  Success = 'success',
}

export type Args = any[];

interface ILoadingStateIdle {
  stage: LoadingStage.Idle;
  value: undefined;
  error: undefined;
}

interface ILoadingStateLoading {
  stage: LoadingStage.Loading;
  value: undefined;
  error: undefined;
}

interface ILoadingStateSuccess<ValueType> {
  stage: LoadingStage.Success;
  value: ValueType;
  error: undefined;
}

interface ILoadingStateError {
  stage: LoadingStage.Error;
  value: undefined;
  error: Error;
}

export type LoadingState<ValueType> =
  ILoadingStateIdle |
  ILoadingStateLoading |
  ILoadingStateSuccess<ValueType> |
  ILoadingStateError;

export type LoadingStateNoError<ValueType> =
  ILoadingStateIdle |
  ILoadingStateLoading |
  ILoadingStateSuccess<ValueType>;

interface ILoadingStateParams<ValueType, LoadArgs> {
  load: (params: LoadArgs, cancelToken: CancelToken) => PromiseLike<ValueType>;
}

// Without overloading, when `load` does not take any `args`, TS infers `args` as unknown instead of empty.
export function useLoadingState<ValueType>(
  props: { load: () => PromiseLike<ValueType>; }
): LoadingState<ValueType> & {
  load: () => void;
  clear: () => void;
};

export function useLoadingState<ValueType, LoadArgs>(
  { load }: ILoadingStateParams<ValueType, LoadArgs>
): LoadingState<ValueType> & {
  load: (params: LoadArgs) => void;
  clear: () => void;
};

export function useLoadingState(
  { load }: ILoadingStateParams<unknown, unknown>,
): LoadingState<unknown> & {
  load: (params: unknown) => void;
  clear: () => void;
} {
  const [state, setStateRaw] = useState<LoadingState<unknown>>({
    stage: LoadingStage.Idle,
    value: undefined,
    error: undefined,
  });

  // Does a shallow equality check to avoid unnecessary re-rendering
  const setState = (targetState: LoadingState<unknown>) => {
    setStateRaw(prevState => (
      (
        prevState.stage === targetState.stage &&
        prevState.value === targetState.value &&
        prevState.error === targetState.error
      ) ?
        prevState :
        targetState
    ));
  };

  const loadQueue = useConstant(() => new TaskQueue());

  const canceller = useRef<undefined | ICanceller>(undefined);

  const clear = useLatestCallback(() => {
    canceller.current?.cancel();

    setState({
      stage: LoadingStage.Idle,
      error: undefined,
      value: undefined,
    });
  });

  const loadAndUpdateState = async (args: unknown, cancelToken: CancelToken) => {
    // Even if we the cancel token is already cancelled when we get unqueued, we
    // still call `load()`, because some code may want to avoid cancelling the
    // intermediate load requests (e.g. when the requests have an important side
    // effect like some kind of server POST request).

    try {
      const result = load(args, cancelToken);

      const value = await result;

      if (!cancelToken.isCancelled()) {
        setState({ stage: LoadingStage.Success, value, error: undefined });
      }
    } catch (error) {
      if (!cancelToken.isCancelled()) {
        setState({ stage: LoadingStage.Error, error, value: undefined });
      }
    }
  };

  const triggerLoad = useLatestCallback((args: unknown) => {
    const prevCanceller = canceller.current;
    const currentCanceller = CancelToken.create();
    canceller.current = currentCanceller;

    loadQueue.push(async () => {
      await loadAndUpdateState(args, currentCanceller.cancelToken);
    });

    // Switch to the loading state immediately, so it does not have to wait for
    // the queue.
    setState({
      stage: LoadingStage.Loading,
      value: undefined,
      error: undefined,
    });

    // Cancel previously pending request
    if (prevCanceller) {
      prevCanceller.cancel();
    }
  });

  // Abort pending requests on unmount
  useEffect(() => {
    return () => {
      if (canceller.current) {
        canceller.current.cancel();
      }
      canceller.current = undefined;
    };
  }, []);

  return {
    load: triggerLoad,
    clear,
    ...state,
  };
}

interface ILoadingStateCompatIdle {
  loadingState: LoadingStage.Idle;
  isIdle: true;
  isLoading: false;
  isSuccess: false;
  isError: false;
  result: undefined;
}

interface ILoadingStateCompatLoading {
  loadingState: LoadingStage.Loading;
  isIdle: false;
  isLoading: true;
  isSuccess: false;
  isError: false;
  result: undefined;
}

interface ILoadingStateCompatSuccess<ValueType> {
  loadingState: LoadingStage.Success;
  isIdle: false;
  isLoading: false;
  isSuccess: true;
  isError: false;
  result: ValueType;
}

export interface ILoadingStateCompatError {
  loadingState: LoadingStage.Error;
  isIdle: false;
  isLoading: false;
  isSuccess: false;
  isError: true;
  result: Error;
}

export type LoadingStateCompat<ValueType> =
  ILoadingStateCompatIdle |
  ILoadingStateCompatLoading |
  ILoadingStateCompatSuccess<ValueType> |
  ILoadingStateCompatError;

type ChildParamsNoError<ValueType, LoadArgs> =
Exclude<LoadingStateCompat<ValueType>, ILoadingStateCompatError> &
ILoadingStateCompatMethods<LoadArgs>;

export function compat<ValueType>(state: LoadingState<ValueType>): LoadingStateCompat<ValueType> {
  const base = {
    isIdle: false,
    isLoading: false,
    isError: false,
    isSuccess: false,
    result: undefined,
  } as const;

  switch (state.stage) {
    case LoadingStage.Idle: return {
      ...base,
      loadingState: state.stage,
      isIdle: true,
    };

    case LoadingStage.Loading: return {
      ...base,
      loadingState: state.stage,
      isLoading: true,
    };

    case LoadingStage.Success: return {
      ...base,
      loadingState: state.stage,
      isSuccess: true,
      result: state.value,
    };

    case LoadingStage.Error: return {
      ...base,
      loadingState: state.stage,
      isError: true,
      result: state.error,
    };
  }
}

interface ILoadingStateCompatMethods<LoadArgs> {
  load: (params: LoadArgs) => void;
  clearResult: () => void;
}

export interface ILoadingStatePropsNoError<ValueType, LoadArgs> extends ILoadingStateParams<ValueType, LoadArgs> {
  children: (loadingState: ChildParamsNoError<ValueType, LoadArgs>) => ReactNode;
}

/**
 * @deprecated Use `useLoadingState()` hook instead
 */
export default function LoadingState<ValueType>(props: {
  load: () => PromiseLike<ValueType>;
  children: (loadingState: LoadingStateCompat<ValueType> & {
      load: () => void;
      clearResult: () => void;
    }) => ReactNode;
}): ReactElement;

export default function LoadingState<ValueType, LoadArgs>(props: {
  load: (params: LoadArgs, cancelToken: CancelToken) => PromiseLike<ValueType>;
  children: (loadingState: LoadingStateCompat<ValueType> & {
      load: (params: LoadArgs) => void;
      clearResult: () => void;
    }) => ReactNode;
}): ReactElement;

/**
 * @deprecated - Prefer useLoadingState instead.
 */
// eslint-disable-next-line @typescript-eslint/no-redeclare
export default function LoadingState({
  children,
  ...params
}: {
  load: (params: unknown, cancelToken: CancelToken) => PromiseLike<unknown>;
  children: (loadingState: LoadingStateCompat<unknown> & {
      load: (params: unknown) => void;
      clearResult: () => void;
    }) => ReactNode;
} | {
  load: () => PromiseLike<unknown>;
  children: (loadingState: LoadingStateCompat<unknown> & {
      load: () => void;
      clearResult: () => void;
    }) => ReactNode;
}): ReactElement {
  const { load, clear: clearResult, ...loadingState } = useLoadingState(params);

  return children({ load: load as any, clearResult, ...compat(loadingState) }) as ReactElement;
}
